import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PlayerCalendarStateProvider } from "./context/PlayerCalendarState";
import { I18nProvider } from "./lib/i18n";
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import PublicInquiry from "./pages/PublicInquiry";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvitation from "./pages/AcceptInvitation";
import PlayerIntake from "./pages/PlayerIntake";
import PrivacyNotice from "./pages/PrivacyNotice";
import WebAppGuide from "./pages/WebAppGuide";
import ChangePassword from "./pages/ChangePassword";
import PlayerToday from "./pages/PlayerToday";
import PlayerDailyCheck from "./pages/PlayerDailyCheck";
import PlayerRequests from "./pages/PlayerRequests";
import PlayerServices from "./pages/PlayerServices";
import PlayerNotifications from "./pages/PlayerNotifications";
import CareManager from "./pages/CareManager";
import CarePlayerRecord from "./pages/CarePlayerRecord";
import CareTrainingPlanOriginal from "./pages/CareTrainingPlanOriginal";
import AdminDashboard from "./pages/AdminDashboard";
import AdminShiftPlan from "./pages/AdminShiftPlan";
import AccountingDashboard from "./pages/AccountingDashboard";
import "./player-ui.css";

function Splash() {
  return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--pc-black)" }}><div className="pc-spinner" /></div>;
}

function Guard({ roles, children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={home(user.role)} replace />;
  return user.role === "player" ? <div className="pc-player-ui">{children}</div> : children;
}

function home(role) {
  return role === "player" ? "/today" : role === "care_manager" ? "/care" : role === "accounting" ? "/accounting" : "/admin";
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <PlayerCalendarStateProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/anfrage" element={<PublicInquiry />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/intake/:token" element={<PlayerIntake />} />
            <Route path="/datenschutz" element={<PrivacyNotice />} />
            <Route path="/hilfe/web-app" element={<WebAppGuide />} />
            <Route path="/change-password" element={<Guarded><ChangePassword /></Guarded>} />
            <Route path="/today" element={<Guard roles={["player"]}><PlayerToday /></Guard>} />
            <Route path="/daily-check" element={<Guard roles={["player"]}><PlayerDailyCheck /></Guard>} />
            <Route path="/requests" element={<Guard roles={["player"]}><PlayerRequests /></Guard>} />
            <Route path="/services" element={<Guard roles={["player"]}><PlayerServices /></Guard>} />
            <Route path="/notifications" element={<Guard roles={["player"]}><PlayerNotifications /></Guard>} />
            <Route path="/care" element={<Guard roles={["care_manager", "admin"]}><CareManager /></Guard>} />
            <Route path="/care/mail" element={<Guard roles={["care_manager", "admin"]}><CareManager mailWindow /></Guard>} />
            <Route path="/care/players/:playerId" element={<Guard roles={["care_manager", "admin"]}><CarePlayerRecord /></Guard>} />
            <Route path="/care/training-plans/:planId/original" element={<Guard roles={["care_manager", "admin"]}><CareTrainingPlanOriginal /></Guard>} />
            <Route path="/care/club-training-plan-reviews/:reviewId/original" element={<Guard roles={["care_manager", "admin"]}><CareTrainingPlanOriginal /></Guard>} />
            <Route path="/admin" element={<Guard roles={["admin"]}><AdminDashboard /></Guard>} />
            <Route path="/admin/shift-plan" element={<Guard roles={["admin"]}><AdminShiftPlan /></Guard>} />
            <Route path="/admin/mail" element={<Guard roles={["admin"]}><CareManager mailWindow /></Guard>} />
            <Route path="/accounting" element={<Guard roles={["accounting"]}><AccountingDashboard /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </PlayerCalendarStateProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

// change-password only needs an authenticated user (any role, even must_change)
function Guarded({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}
